import React, { useRef } from 'react';

/**
 * Pure: how the numeric `value` is shown in the input. 0 → '' (so the
 * placeholder "0" shows through in gray); everything else → its string
 * form. Extracted for unit tests.
 */
export function formatHoursDisplay(value) {
  if (value === 0 || value === '' || value === null || value === undefined) return '';
  return String(value);
}

/**
 * Pure: parse a typed value into the canonical 0..8 integer. Empty
 * collapses to 0; non-finite collapses to 0; otherwise clamp [0, 8].
 */
export function parseHoursInputValue(raw) {
  if (raw === '' || raw === null || raw === undefined) return 0;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(8, n));
}

/**
 * Ghost-zero hours input — 0 renders as a gray placeholder rather than a
 * literal "0" the user has to delete. On focus the field selects its
 * entire content so typing a digit replaces cleanly.
 *
 * Props:
 *   value    — number 0..8
 *   onChange — (n: number) => void
 *   error    — boolean; swaps border to #B30745 with focus-ring
 *   size     — 'md' | 'sm'
 *   disabled — boolean
 */
export function HoursInput({
  value,
  onChange,
  error = false,
  size = 'md',
  disabled = false,
}) {
  const isSm = size === 'sm';
  const height = isSm ? 38 : 44;
  const width = isSm ? 70 : 96;
  const paddingInput = isSm ? '0 4px 0 10px' : '0 4px 0 12px';
  const paddingUnit = isSm ? '0 10px 0 2px' : '0 12px 0 4px';

  const inputRef = useRef(null);

  const displayValue = formatHoursDisplay(value);

  const handleChange = (e) => onChange(parseHoursInputValue(e.target.value));
  const handleFocus = (e) => e.target.select();
  const handleBlur = (e) => {
    const raw = e.target.value;
    if (raw === '' || !Number.isFinite(parseInt(raw, 10))) {
      onChange(0);
    }
  };

  const borderColor = error ? '#B30745' : 'var(--sp-border)';
  const focusRing = error ? '0 0 0 3px rgba(179,7,69,0.1)' : undefined;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width,
        height,
        flexShrink: 0,
        border: `1px solid ${borderColor}`,
        borderRadius: 10,
        background: '#fff',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onFocusCapture={(e) => {
        if (focusRing) e.currentTarget.style.boxShadow = focusRing;
      }}
      onBlurCapture={(e) => {
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <input
        ref={inputRef}
        type="number"
        min={0}
        max={8}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder="0"
        style={{
          flex: 1,
          minWidth: 0,
          padding: paddingInput,
          border: 0,
          outline: 0,
          fontSize: isSm ? 14 : 15,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          background: 'transparent',
          textAlign: 'right',
          color: 'var(--sp-fg-1)',
        }}
      />
      <span
        style={{
          padding: paddingUnit,
          color: 'var(--sp-fg-3)',
          fontSize: 12,
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        h
      </span>
    </div>
  );
}
